import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { EMPTY, forkJoin } from 'rxjs'
import { catchError, map, switchMap, take } from 'rxjs/operators'

import type { InteractionContext } from '@app/models/interactions/context'
import type { Group } from '@app/models/interactions/group/incoming'
import { NotificationsChannelService } from '@app/modules/notifications'
import type { InteractionContextResponse } from '@app/services/interaction-context.service'
import { InteractionContextService } from '@app/services/interaction-context.service'
import { MemberService } from '@app/services/member.service'
import * as fromFeature from '@store/interaction-context'
import * as fromInteractionContext from '@store/interaction-context/index'

const adaptHasUnread = (response: InteractionContextResponse, isBenefactor: boolean): Record<Group, boolean> => ({
  locked_granters: isBenefactor ? response.locked_approved_created_photo_requests : false,
  locked_question_granters: false,
  locked_videos_granters: false,
  unlocked_granters: isBenefactor
    ? response.unlocked_approved_created_photo_requests
    : response.locked_approved_created_photo_requests,
  unlocked_question_granters: false,
  unlocked_videos_granters: false,
  pending_requesters: response.has_pending_received_photo_requests,
  admirers: response.has_pending_admirers,
  visitors: response.has_pending_profile_views,
  messages: false,
})

const adapt = (response: InteractionContextResponse, isBenefactor: boolean): InteractionContext => ({
  hasUnread: adaptHasUnread(response, isBenefactor),
  favorited_target_ids: response.favorited_target_ids,
  messages_dot_ids: response.messages_dot_ids,
})

@Injectable()
export class InteractionContextEffects {
  private readonly actions$ = inject(Actions)
  private readonly interactionContextService = inject(InteractionContextService)
  private readonly notificationsChannelService = inject(NotificationsChannelService)
  private readonly memberService = inject(MemberService)

  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromFeature.Actions.load),
      switchMap(() =>
        forkJoin([
          this.interactionContextService.load().pipe(catchError(() => EMPTY)),
          this.memberService.account$.pipe(take(1)),
        ]),
      ),
      map(([response, account]) => {
        const data = adapt(response, account.benefactor)
        return fromInteractionContext.Actions.loaded({ data })
      }),
    ),
  )

  readonly reconnect$ = createEffect(() =>
    this.notificationsChannelService.channel$.pipe(
      switchMap(channel => channel.reconnects$),
      map(() => fromInteractionContext.Actions.load()),
    ),
  )
}
